import {
  compose,
  lifecycle,
  withStateHandlers,
  withHandlers,
  flattenProp,
} from 'recompose';
import { connect } from 'react-redux';
import { updateModelWorkingState } from 'reducers/models/actions';
import { clearAdvancedFilters } from 'reducers/modelAdvanced/actions';
import {
  model,
  currentSelectedQuantity,
  modelRemainingQuota,
  modelSubmittedTotalWorkingStateCalculated,
  workingStateLoaded,
  staticDataLoaded,
  inCartQuantity,
} from 'reducers/models/selectors';
import { theme } from 'reducers/dashboard/selectors';
import { addProps, renderNothingIfPropFalsy } from 'utils/recompose';
import { injectIntl } from 'react-intl';
import combineSelectors from 'utils/combineSelectors';
import styles from './styles.module.scss';
import { advancedChartIsSelected } from 'reducers/modelPipeline/selectors';

const mapStateToProps = combineSelectors({
  advancedChartIsSelected,
  currentSelectedQuantity,
  inCartQuantity,
  model,
  modelRemainingQuota,
  modelSubmittedTotalWorkingStateCalculated,
  staticDataLoaded,
  theme,
  workingStateLoaded,
});

const mapDispatchToProps = {
  updateModelWorkingState,
  clearAdvancedFilters,
};

export function componentDidMount() {
  this.props.clearAdvancedFilters();
  this.props.setQuantityError();
}

export function componentDidUpdate(prevProps) {
  const {
    currentSelectedQuantity,
    modelRemainingQuota,
    hasScrolled,
    collapsedChart,
    scrollTop,
  } = this.props;
  if (
    prevProps.modelRemainingQuota !== modelRemainingQuota ||
    prevProps.currentSelectedQuantity !== currentSelectedQuantity
  ) {
    this.props.setQuantityError();
  }
  if (hasScrolled && collapsedChart && scrollTop) {
    document.documentElement.scrollTop = scrollTop;
  }
}

export const initialState = {
  quantityWarning: null,
  searchOptions: [],
};

export const setSearchOptions = () => searchOptions => {
  return { searchOptions };
};

export const triggerSearch = ({ setSearchOptions, showSearch }) => ({
  functionalGroupCodes,
}) => {
  showSearch();
  setSearchOptions(functionalGroupCodes);
};

export const setQuantityError = (state, props) => () => {
  const {
    currentSelectedQuantity,
    quota,
    modelSubmittedTotalWorkingStateCalculated,
    inCartQuantity,
  } = props;

  const remainingQuota =
    quota - modelSubmittedTotalWorkingStateCalculated - inCartQuantity;
  const templatesQuantity = currentSelectedQuantity - inCartQuantity;
  const hasRemainingQuota = remainingQuota > 0;

  if (remainingQuota < templatesQuantity && hasRemainingQuota) {
    return {
      quantityWarning: {
        message: `The quantity below adds up to ${templatesQuantity}, you need to remove`,
        difference: templatesQuantity - remainingQuota,
      },
    };
  } else if (remainingQuota > templatesQuantity && hasRemainingQuota) {
    return {
      quantityWarning: {
        message: `The quantity below adds up to ${currentSelectedQuantity}, you need to add`,
        difference: remainingQuota - currentSelectedQuantity,
      },
    };
  } else {
    return { quantityWarning: null };
  }
};

export const lockedBackground = ({ theme }) => ({
  backgroundColor: theme.palette.primary,
});

export const modelDetailStyles = ({
  advancedChartIsSelected,
  chartSticky,
  collapsedChart,
  selectedOptions,
}) => {
  let hasSelectedOptions = selectedOptions && selectedOptions.length > 0;

  if (collapsedChart && !hasSelectedOptions) {
    return styles.modelDetailsWithCollapsedHeader;
  } else if (collapsedChart && hasSelectedOptions) {
    return styles.modelDetailsExpandedSearchOptionCollapsedChart;
  } else if (chartSticky && !advancedChartIsSelected) {
    return styles.modelDetailsWithChartSticky;
  } else {
    return styles.modelDetails;
  }
};

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  flattenProp('model'),
  injectIntl,
  withStateHandlers(initialState, {
    setSearchOptions,
    setQuantityError,
  }),
  withHandlers({
    triggerSearch,
  }),
  addProps({ lockedBackground, modelDetailStyles }),
  lifecycle({ componentDidMount, componentDidUpdate }),
  renderNothingIfPropFalsy('model'),
);
